import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { SimplePDFViewer } from './SimplePDFViewer';
import { LibreOfficeOnlineViewer } from './LibreOfficeOnlineViewer';
import { WordOnlineViewer } from './WordOnlineViewer';
import { supabase } from '@/integrations/supabase/client';
import { FileText, Download, Eye, BookOpen, FileIcon } from 'lucide-react';

interface DocumentViewerProps {
  fileUrl: string;
  fileName: string;
  fileType: string;
  className?: string;
  preferredViewer?: 'libreoffice' | 'microsoft' | 'pdf';
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  fileUrl,
  fileName,
  fileType,
  className = '',
  preferredViewer = 'libreoffice'
}) => {
  const { toast } = useToast();
  const [isConverting, setIsConverting] = useState(false);
  const [currentViewer, setCurrentViewer] = useState<'libreoffice' | 'microsoft' | 'pdf'>(preferredViewer);
  const [convertedPdfUrl, setConvertedPdfUrl] = useState<string | null>(null);

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const isSupported = (type: string, filename: string) => {
    const ext = getFileExtension(filename);
    return ext === 'pdf' || ext === 'docx' || ext === 'doc' || ext === 'odt' || type === 'application/pdf';
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download started",
      description: `Downloading ${fileName}`,
    });
  };

  const convertWithLibreOffice = async () => {
    try {
      setIsConverting(true);
      
      toast({
        title: "Converting document",
        description: "Converting document to PDF with LibreOffice...",
      });

      const { data, error } = await supabase.functions.invoke('convert-with-libreoffice', {
        body: {
          fileUrl: fileUrl,
          fileName: fileName,
          targetFormat: 'pdf'
        }
      });

      if (error) {
        throw error;
      }

      if (data && data.success) {
        setConvertedPdfUrl(data.convertedUrl);
        setCurrentViewer('pdf');
        toast({
          title: "Conversion successful",
          description: "Document converted to PDF with LibreOffice",
        });
      } else {
        toast({
          title: "LibreOffice conversion failed",
          description: data?.message || "LibreOffice conversion service encountered an error",
          variant: "destructive",
        });
      }
      
    } catch (error: any) {
      console.error('Error converting with LibreOffice:', error);
      
      toast({
        title: "Conversion failed",
        description: "Unable to convert document with LibreOffice. Try downloading the file instead.",
        variant: "destructive",
      });
    } finally {
      setIsConverting(false);
    }
  };

  // Check file type
  const ext = getFileExtension(fileName);
  const isPDF = ext === 'pdf' || fileType === 'application/pdf';
  const isWord = ext === 'docx' || ext === 'doc' || fileType.includes('word');
  const isODT = ext === 'odt' || fileType === 'application/vnd.oasis.opendocument.text';
  const isOfficeDoc = isWord || isODT;

  if (!isSupported(fileType, fileName)) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold mb-2">Unsupported File Type</h3>
          <p className="text-sm text-muted-foreground mb-4">
            This file type cannot be previewed in the browser.
          </p>
          <Badge variant="outline" className="mb-4">
            {fileName}
          </Badge>
          <div>
            <Button onClick={handleDownload} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Download File
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Render PDF viewer (original or converted)
  if (isPDF || (currentViewer === 'pdf' && convertedPdfUrl)) {
    const pdfUrl = convertedPdfUrl || fileUrl;
    return (
      <div className={className}>
        <SimplePDFViewer 
          fileUrl={pdfUrl} 
          fileName={fileName}
        />
      </div>
    );
  }

  // Render office document viewers
  if (isOfficeDoc) {
    return (
      <div className={className}>
        {/* Viewer Selection */}
        <div className="flex gap-2 p-2 border-b bg-muted/20">
          <Button
            size="sm"
            variant={currentViewer === 'libreoffice' ? 'default' : 'outline'}
            onClick={() => setCurrentViewer('libreoffice')}
          >
            <BookOpen className="w-3 h-3 mr-1" />
            LibreOffice
          </Button>
          <Button
            size="sm"
            variant={currentViewer === 'microsoft' ? 'default' : 'outline'}
            onClick={() => setCurrentViewer('microsoft')}
          >
            <FileIcon className="w-3 h-3 mr-1" />
            Microsoft
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={convertWithLibreOffice}
            disabled={isConverting}
          >
            <Eye className="w-3 h-3 mr-1" />
            {isConverting ? 'Converting...' : 'Convert to PDF'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownload}
          >
            <Download className="w-3 h-3 mr-1" />
            Download
          </Button>
        </div>

        {/* Viewer Content */}
        {currentViewer === 'libreoffice' ? (
          <LibreOfficeOnlineViewer
            documentUrl={fileUrl}
            fileName={fileName}
            height="600px"
          />
        ) : currentViewer === 'microsoft' ? (
          <WordOnlineViewer
            documentUrl={fileUrl}
            fileName={fileName}
            height="600px"
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className={className}>
      <Card className="p-6">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold mb-2">Unsupported File Type</h3>
          <p className="text-sm text-muted-foreground mb-4">
            This file type cannot be previewed in the browser.
          </p>
          <Badge variant="outline" className="mb-4">
            {fileName}
          </Badge>
          <Button onClick={handleDownload} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Download File
          </Button>
        </div>
      </Card>
    </div>
  );
};